import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import i18next from 'i18next';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useOnValueChange } from '@hooks-clean/index';
import {
    SubjectMonitoringReviewTypeDto,
    SubjectMonitoringReviewTypePostDto,
} from '@interfaces-clean/subject-monitoring-review-type.interface';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

const COMPONENT_NAME = 'edit-review-type-dialog';

export type EditReviewTypeForm = SubjectMonitoringReviewTypePostDto;

type EditReviewTypeDialogProps = {
    record: SubjectMonitoringReviewTypeDto;
    existingNames: string[];
    onClose: () => void;
    onSubmit: (data: EditReviewTypeForm) => void;
    isSubmitting: boolean;
};

const EditReviewTypeDialog = ({
    record,
    existingNames,
    onClose,
    onSubmit,
    isSubmitting,
}: EditReviewTypeDialogProps) => {
    const { t } = useTranslation('admin');
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const existingNamesSet = useMemo(
        () => new Set(existingNames.map((n) => n.toLocaleLowerCase().trim())),
        [existingNames],
    );

    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid },
        trigger,
    } = useForm<EditReviewTypeForm>({
        defaultValues: {
            active: record.active ?? true,
            name: record.name ?? '',
            description: record.description ?? '',
        },
        mode: 'all',
    });

    useOnValueChange(existingNamesSet, () => {
        if (isDirty) trigger().catch((err) => window.console.error(err));
    });

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isDirty, setComponentHasChanges]);

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME)) {
            const confirmed = await confirmDiscardChanges(`${COMPONENT_NAME}-confirmation`);
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };

    const isFormInvalid = !isValid || !isDirty || isSubmitting;

    const createdInfo = record.createdOn
        ? `${stringDateWithoutTimeFromUtc(record.createdOn, 'DD-MMM-YYYY')} by ${record.createdBy ?? ''}`
        : undefined;
    const updatedInfo = record.updatedOn
        ? `${stringDateWithoutTimeFromUtc(record.updatedOn, 'DD-MMM-YYYY')} by ${record.updatedBy ?? ''}`
        : undefined;

    return (
        <DraggableDialog
            dataTestId="edit-review-type-dialog"
            title={t('smReviewTypes.editDialog.title', { id: record.recordId?.toString() })}
            primaryBtnLabel={t('common:save')}
            secondaryBtnLabel={t('common:cancel')}
            primaryBtnDisabled={isFormInvalid}
            onSubmit={() => handleSubmit(onSubmit)()}
            onClose={onCloseHandler}
            initialWidth={559}
            initialHeight={460}
            bodyPadding="none"
            createdUpdatedInfo={<CreatedUpdatedInfo created={createdInfo} updated={updatedInfo} width={500} />}
            createdUpdatedBackgroundColour="gray"
        >
            <form id="edit-review-type-form" className="k-w-full k-d-flex k-flex-column k-gap-2 k-px-3">
                <Controller
                    control={control}
                    name="active"
                    render={({ field: { onChange, value } }) => (
                        <CoreCheckbox
                            data-testid="edit-review-type-active"
                            onChange={onChange}
                            value={value}
                            label={t('smReviewTypes.fields.active')}
                        />
                    )}
                />
                <TextBoxController
                    control={control}
                    name="name"
                    data-testid="edit-review-type-name"
                    label={`${t('smReviewTypes.fields.name')}*`}
                    maxLength={70}
                    required
                    validateRules={{
                        uniqueValue: (value) => {
                            const recordName = record.name?.toLocaleLowerCase().trim();
                            const valueString = ((value ?? '') as string).toLocaleLowerCase().trim();
                            if (valueString !== recordName && existingNamesSet.has(valueString)) {
                                return i18next.t('admin:smReviewTypes.validation.nameInUse');
                            }
                            return true;
                        },
                        whitespace: (value) => {
                            if (value && !(value as string).trim().length)
                                return i18next.t('ra:requiredFieldError') as string;
                            return true;
                        },
                    }}
                />
                <TextAreaController
                    control={control}
                    name="description"
                    data-testid="edit-review-type-description"
                    label={t('smReviewTypes.fields.description')}
                    maxLength={3000}
                    rows={8}
                    resizable="none"
                />
            </form>
        </DraggableDialog>
    );
};

export default EditReviewTypeDialog;
